import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Calendar, Clock, Users, Activity, AlertTriangle,
  CheckCircle, MapPin, TrendingUp, Sparkles,
  User, Stethoscope, Heart, Shield, Zap, Target
} from "lucide-react";

const AISchedulingPage = () => {
  const { user, loading: authLoading } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('optimize');
  const [loading, setLoading] = useState(false);
  const [scheduleResult, setScheduleResult] = useState(null);
  const [acuityGuidelines, setAcuityGuidelines] = useState(null);
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    patient_id: '',
    chart_id: '',
    current_acuity: 5,
    recommendations: [],
    compliance_gaps: []
  });

  useEffect(() => {
    if (!authLoading && (!user || !['admin', 'staff'].includes(user.role))) {
      navigate('/');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    loadPatients();
    loadAcuityGuidelines(5);
  }, []);

  useEffect(() => {
    loadAcuityGuidelines(formData.current_acuity);
  }, [formData.current_acuity]);

  const loadPatients = async () => {
    try {
      const response = await api.get('/ehr/charts/list');
      setPatients(response.data.charts || []);
    } catch (error) {
      console.error("Failed to load patients:", error);
      // Demo patients
      setPatients([
        { id: 'chart_001', demographics: { first_name: 'John', last_name: 'Smith' }, diagnosis: 'Lung Cancer', acuity: 7 },
        { id: 'chart_002', demographics: { first_name: 'Mary', last_name: 'Johnson' }, diagnosis: 'CHF', acuity: 5 },
        { id: 'chart_003', demographics: { first_name: 'Robert', last_name: 'Williams' }, diagnosis: 'COPD', acuity: 8 },
        { id: 'chart_004', demographics: { first_name: 'Patricia', last_name: 'Davis' }, diagnosis: 'Dementia', acuity: 6 },
      ]);
    }
  };

  const loadAcuityGuidelines = async (acuity) => {
    try {
      const response = await api.get(`/ehr/ai-scheduling/acuity-guidelines/${acuity}`);
      setAcuityGuidelines(response.data);
    } catch (error) {
      console.error("Failed to load acuity guidelines:", error);
    }
  };

  const handleOptimizeSchedule = async () => {
    if (!selectedPatient) {
      toast.error("Please select a patient");
      return;
    }

    setLoading(true);
    try {
      // Build recommendations from care plans if available
      const demoRecommendations = [
        { category: 'symptom_management', priority: 'high', title: 'Pain assessment needed', timeframe: 'within_24h', discipline: ['RN'], requires_md_approval: false },
        { category: 'psychosocial', priority: 'medium', title: 'Anxiety support', timeframe: 'within_48h', discipline: ['SW'], requires_md_approval: false },
        { category: 'caregiver', priority: 'medium', title: 'Caregiver education', timeframe: 'next_visit', discipline: ['RN', 'SW'], requires_md_approval: false }
      ];

      // Build compliance gaps
      const demoGaps = formData.current_acuity >= 7 ? [
        { type: 'hope_assessment', description: 'HOPE assessment overdue', days_overdue: 3 }
      ] : [];

      const response = await api.post('/ehr/ai-scheduling/optimize', {
        patient_id: selectedPatient.id,
        chart_id: selectedPatient.id,
        current_acuity: formData.current_acuity,
        recommendations: demoRecommendations,
        compliance_gaps: demoGaps,
        staff_available: [],
        existing_visits: []
      });
      
      setScheduleResult(response.data);
      toast.success(`Schedule optimized: ${response.data.suggested_visits?.length || 0} visits recommended`);
    } catch (error) {
      console.error("Schedule optimization failed:", error);
      toast.error("Failed to optimize schedule");
    } finally {
      setLoading(false);
    }
  };

  const getUrgencyColor = (urgency) => {
    switch (urgency) {
      case 'immediate': return 'destructive';
      case 'within_24h': return 'default';
      case 'within_48h': return 'secondary';
      case 'next_visit': return 'outline';
      default: return 'outline';
    }
  };

  const getVisitTypeIcon = (type) => {
    switch (type) {
      case 'rn': return <Stethoscope className="w-4 h-4" />;
      case 'sw': return <Heart className="w-4 h-4" />;
      case 'chaplain': return <Shield className="w-4 h-4" />;
      case 'md': return <User className="w-4 h-4" />;
      case 'hha': return <Users className="w-4 h-4" />;
      default: return <Calendar className="w-4 h-4" />;
    }
  };

  const getRiskColor = (assessment) => {
    if (assessment.includes('HIGH')) return 'text-red-600 bg-red-50';
    if (assessment.includes('MODERATE')) return 'text-yellow-600 bg-yellow-50';
    return 'text-green-600 bg-green-50';
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-indigo-50">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <Sparkles className="w-8 h-8 text-indigo-600" />
                AI Visit Scheduling Optimization
              </h1>
              <p className="text-muted-foreground mt-1">
                Intelligent visit scheduling based on care plans and patient acuity
              </p>
            </div>
            <Button variant="outline" onClick={() => navigate('/ehr-compliance')}>
              Back to EHR
            </Button>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="optimize" data-testid="tab-optimize">
              <Sparkles className="w-4 h-4 mr-2" />
              Optimize Schedule
            </TabsTrigger>
            <TabsTrigger value="guidelines" data-testid="tab-guidelines">
              <Target className="w-4 h-4 mr-2" />
              Acuity Guidelines
            </TabsTrigger>
          </TabsList>

          {/* Optimize Schedule Tab */}
          <TabsContent value="optimize">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Input Panel */}
              <div className="space-y-4">
                {/* Patient Selection */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Users className="w-5 h-5" />
                      Select Patient
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="max-h-48 overflow-y-auto space-y-2">
                      {patients.map(patient => (
                        <div
                          key={patient.id}
                          className={`p-3 rounded-lg border cursor-pointer transition-all ${
                            selectedPatient?.id === patient.id
                              ? 'border-indigo-500 bg-indigo-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          onClick={() => {
                            setSelectedPatient(patient);
                            setFormData(prev => ({
                              ...prev,
                              patient_id: patient.id,
                              chart_id: patient.id,
                              current_acuity: patient.acuity || 5
                            }));
                          }}
                          data-testid={`patient-${patient.id}`}
                        >
                          <p className="font-medium">
                            {patient.demographics?.first_name} {patient.demographics?.last_name}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {patient.diagnosis} • Acuity: {patient.acuity || 'N/A'}
                          </p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Acuity Setting */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Activity className="w-5 h-5" />
                      Patient Acuity
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label>Current Acuity Level: {formData.current_acuity}/10</Label>
                      <Slider
                        value={[formData.current_acuity]}
                        onValueChange={(value) => setFormData(prev => ({ ...prev, current_acuity: value[0] }))}
                        min={1}
                        max={10}
                        step={1}
                        className="mt-2"
                      />
                      <div className="flex justify-between text-xs text-muted-foreground mt-1">
                        <span>Stable</span>
                        <span>Crisis</span>
                      </div>
                    </div>

                    {acuityGuidelines && (
                      <div className={`p-3 rounded-lg ${
                        formData.current_acuity >= 7 ? 'bg-red-50 text-red-700' :
                        formData.current_acuity >= 4 ? 'bg-yellow-50 text-yellow-700' :
                        'bg-green-50 text-green-700'
                      }`}>
                        <p className="text-sm font-medium">{acuityGuidelines.risk_category} Risk</p>
                        <p className="text-xs mt-1">{acuityGuidelines.notes}</p>
                      </div>
                    )}

                    <Button 
                      onClick={handleOptimizeSchedule}
                      disabled={loading || !selectedPatient}
                      className="w-full"
                      data-testid="optimize-schedule-btn"
                    >
                      {loading ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                      ) : (
                        <Sparkles className="w-4 h-4 mr-2" />
                      )}
                      Optimize Schedule
                    </Button>
                  </CardContent>
                </Card>
              </div>

              {/* Results Panel */}
              <div className="lg:col-span-2 space-y-4">
                {scheduleResult ? (
                  <>
                    {/* Summary Card */}
                    <Card className="border-indigo-200">
                      <CardContent className="pt-6">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div className={`p-4 rounded-lg ${getRiskColor(scheduleResult.risk_assessment)}`}>
                            <p className="text-sm font-medium">Risk Assessment</p>
                            <p className="text-lg font-bold">{scheduleResult.risk_assessment?.split(' - ')[0]}</p>
                          </div>
                          <div className="p-4 rounded-lg bg-indigo-50 text-indigo-700">
                            <p className="text-sm font-medium">Suggested Visits</p>
                            <p className="text-lg font-bold">{scheduleResult.suggested_visits?.length || 0}</p>
                          </div>
                          <div className="p-4 rounded-lg bg-slate-50">
                            <p className="text-sm font-medium">Next Review</p>
                            <p className="text-lg font-bold">{scheduleResult.next_review}</p>
                          </div>
                        </div>

                        <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                          <p className="text-sm">{scheduleResult.schedule_summary}</p>
                        </div>

                        {scheduleResult.compliance_alerts?.length > 0 && (
                          <div className="mt-3">
                            {scheduleResult.compliance_alerts.map((alert, idx) => (
                              <div key={idx} className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 p-2 rounded mt-1">
                                <AlertTriangle className="w-4 h-4" />
                                {alert}
                              </div>
                            ))}
                          </div>
                        )}

                        {scheduleResult.optimization_notes?.length > 0 && (
                          <div className="mt-3 space-y-1">
                            {scheduleResult.optimization_notes.map((note, idx) => (
                              <div key={idx} className="flex items-center gap-2 text-sm text-blue-700">
                                <Zap className="w-4 h-4" />
                                {note}
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* Suggested Visits */}
                    <Card>
                      <CardHeader>
                        <CardTitle>Suggested Visit Schedule</CardTitle>
                        <CardDescription>
                          Visits prioritized by urgency and care plan recommendations
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {scheduleResult.suggested_visits?.map((visit, idx) => (
                            <div 
                              key={visit.id || idx} 
                              className={`p-4 rounded-lg border ${
                                visit.urgency === 'immediate' ? 'border-red-300 bg-red-50' :
                                visit.urgency === 'within_24h' ? 'border-orange-300 bg-orange-50' :
                                'border-gray-200 bg-white'
                              }`}
                            >
                              <div className="flex items-start justify-between">
                                <div className="flex items-start gap-3">
                                  <div className={`p-2 rounded-lg ${
                                    visit.visit_type === 'rn' ? 'bg-blue-100' :
                                    visit.visit_type === 'sw' ? 'bg-purple-100' :
                                    visit.visit_type === 'chaplain' ? 'bg-green-100' :
                                    'bg-gray-100'
                                  }`}>
                                    {getVisitTypeIcon(visit.visit_type)}
                                  </div>
                                  <div>
                                    <p className="font-medium">{visit.visit_type.toUpperCase()} Visit</p>
                                    <p className="text-sm text-muted-foreground">{visit.reason}</p>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <Badge variant={getUrgencyColor(visit.urgency)}>
                                    {visit.urgency.replace('_', ' ')}
                                  </Badge>
                                  <p className="text-xs text-muted-foreground mt-1">
                                    Priority: {Math.round(visit.priority_score)}
                                  </p>
                                </div>
                              </div>
                              
                              <div className="mt-3 flex items-center gap-4 text-sm text-muted-foreground">
                                <div className="flex items-center gap-1">
                                  <Calendar className="w-4 h-4" />
                                  {visit.suggested_date}
                                </div>
                                <div className="flex items-center gap-1">
                                  <Clock className="w-4 h-4" />
                                  {visit.duration_minutes} min
                                </div>
                                {visit.compliance_driven && (
                                  <Badge variant="secondary" className="text-xs">
                                    <Shield className="w-3 h-3 mr-1" />
                                    Compliance
                                  </Badge>
                                )}
                              </div>

                              {visit.suggested_time_window && (
                                <p className="text-xs text-muted-foreground mt-2">
                                  <Clock className="w-3 h-3 inline mr-1" />
                                  {visit.suggested_time_window}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </>
                ) : (
                  <Card>
                    <CardContent className="py-16 text-center">
                      <Sparkles className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-30" />
                      <h3 className="text-lg font-medium mb-2">No Schedule Generated Yet</h3>
                      <p className="text-muted-foreground mb-4">
                        Select a patient and set their acuity level, then click "Optimize Schedule" to generate AI-powered visit recommendations.
                      </p>
                      <div className="flex items-center justify-center gap-4 text-sm text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          Care Plan Integration
                        </div>
                        <div className="flex items-center gap-1">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          Acuity-Based Scheduling
                        </div>
                        <div className="flex items-center gap-1">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          Compliance Tracking
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Acuity Guidelines Tab */}
          <TabsContent value="guidelines">
            <Card>
              <CardHeader>
                <CardTitle>Acuity-Based Visit Frequency Guidelines</CardTitle>
                <CardDescription>
                  Recommended weekly visit frequency based on patient acuity level
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2 px-3">Acuity</th>
                        <th className="text-left py-2 px-3">Risk Level</th>
                        <th className="text-center py-2 px-3">RN Visits/Week</th>
                        <th className="text-center py-2 px-3">SW Visits/Week</th>
                        <th className="text-center py-2 px-3">HHA Visits/Week</th>
                        <th className="text-center py-2 px-3">Chaplain/Week</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(acuity => {
                        const guidelines = {
                          1: { rn: 1, sw: 0.5, hha: 2, chaplain: 0.25, risk: 'Low' },
                          2: { rn: 1, sw: 0.5, hha: 3, chaplain: 0.25, risk: 'Low' },
                          3: { rn: 2, sw: 0.5, hha: 3, chaplain: 0.5, risk: 'Low' },
                          4: { rn: 2, sw: 1, hha: 4, chaplain: 0.5, risk: 'Moderate' },
                          5: { rn: 3, sw: 1, hha: 5, chaplain: 1, risk: 'Moderate' },
                          6: { rn: 3, sw: 1, hha: 5, chaplain: 1, risk: 'Moderate' },
                          7: { rn: 4, sw: 2, hha: 7, chaplain: 1, risk: 'High' },
                          8: { rn: 5, sw: 2, hha: 7, chaplain: 2, risk: 'High' },
                          9: { rn: 7, sw: 3, hha: 7, chaplain: 3, risk: 'Crisis' },
                          10: { rn: 14, sw: 7, hha: 7, chaplain: 7, risk: 'Imminent' }
                        }[acuity];
                        
                        return (
                          <tr key={acuity} className={`border-b ${
                            acuity >= 9 ? 'bg-red-50' :
                            acuity >= 7 ? 'bg-orange-50' :
                            acuity >= 4 ? 'bg-yellow-50' :
                            'bg-green-50'
                          }`}>
                            <td className="py-2 px-3 font-medium">{acuity}</td>
                            <td className="py-2 px-3">
                              <Badge variant={
                                guidelines.risk === 'Low' ? 'outline' :
                                guidelines.risk === 'Moderate' ? 'secondary' :
                                guidelines.risk === 'High' ? 'default' :
                                'destructive'
                              }>
                                {guidelines.risk}
                              </Badge>
                            </td>
                            <td className="text-center py-2 px-3">{guidelines.rn}</td>
                            <td className="text-center py-2 px-3">{guidelines.sw}</td>
                            <td className="text-center py-2 px-3">{guidelines.hha}</td>
                            <td className="text-center py-2 px-3">{guidelines.chaplain}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <h4 className="font-medium mb-2">Acuity Score Factors</h4>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      <li>• Symptom severity and stability</li>
                      <li>• Functional status (ADL dependency)</li>
                      <li>• Psychosocial complexity</li>
                      <li>• Caregiver capability and availability</li>
                      <li>• Disease trajectory and prognosis</li>
                    </ul>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <h4 className="font-medium mb-2">Visit Frequency Adjustments</h4>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      <li>• Increase for symptom escalation</li>
                      <li>• Increase for new admission (first 30 days)</li>
                      <li>• Increase for active dying phase</li>
                      <li>• Adjust per care plan recommendations</li>
                      <li>• Consider compliance requirements</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AISchedulingPage;
